import { useMemo } from 'react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { OverviewSection } from '@/types/teacherDashboard';
import { formatInstituteDate } from '@/features/teacher/utils';

export interface DailyAttendanceCardProps {
  /**
   * The sections the caller teaches on the day, as the overview endpoint
   * returns them. Narrowing to the day's timetable is the caller's job — the
   * endpoint returns every allotted section, taught that day or not.
   */
  sections: OverviewSection[];
  /** `YYYY-MM-DD` — the day the registers are for. */
  date: string;
  /** Whether that day is the institute's today, so the copy says "today" only then. */
  isToday: boolean;
  isLoading?: boolean;
}

/**
 * How many children were in on the day, across the sections the caller teaches.
 *
 * Read from the day's registers rather than from anything on this screen: the
 * daily report says what was taught, and attendance is a separate record taken
 * by a separate screen. Totalled across sections, and counted once each — a
 * teacher who takes 8-A for two subjects has one 8-A register, not two.
 *
 * **A register nobody took is not a class that was all absent.** Sections still
 * unmarked are left out of the ring and named underneath, so a 100% morning
 * over one register is never mistaken for a 100% morning over four.
 */
export function DailyAttendanceCard({
  sections,
  date,
  isToday,
  isLoading = false,
}: DailyAttendanceCardProps) {
  const totals = useMemo(() => {
    // Keyed by section, so a teacher holding two allotments in one class does
    // not count its children twice.
    const bySection = new Map(sections.map((section) => [section.classSectionUuid, section]));
    const marked = [...bySection.values()].filter((section) => section.attendance.isMarked);

    return {
      present: marked.reduce((sum, s) => sum + s.attendance.present + s.attendance.late, 0),
      absent: marked.reduce((sum, s) => sum + s.attendance.absent, 0),
      leave: marked.reduce((sum, s) => sum + s.attendance.leave, 0),
      students: marked.reduce((sum, s) => sum + s.totalStudents, 0),
      markedSections: marked.length,
      totalSections: bySection.size,
    };
  }, [sections]);

  const day = isToday ? 'today' : `on ${formatInstituteDate(date)}`;
  const counted = totals.present + totals.absent + totals.leave;
  const percent = counted === 0 ? null : Math.round((totals.present / counted) * 100);

  const slices: DonutSlice[] = [
    // Late children were in class, so they are counted here — and the label
    // says so, or the figure reads as larger than the register's "Present".
    { key: 'present', label: 'Present incl. late', value: totals.present, stroke: 'stroke-emerald-500', dot: 'bg-emerald-500' },
    { key: 'absent', label: 'Absent', value: totals.absent, stroke: 'stroke-rose-500', dot: 'bg-rose-500' },
    { key: 'leave', label: 'Leave', value: totals.leave, stroke: 'stroke-violet-500', dot: 'bg-violet-500' },
  ];

  return (
    <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
      <h3 className="text-base font-bold tracking-normal text-ink">Daily Attendance</h3>
      <p className="mt-0.5 text-xs font-medium text-ink-3">
        Across the classes you teach {day}
      </p>

      {isLoading ? (
        <div className="mt-4">
          <SkeletonLines count={4} />
        </div>
      ) : counted === 0 ? (
        <p className="mt-5 text-sm font-medium text-ink-3">
          {totals.totalSections === 0
            ? 'You have no classes on this day, so there is no register to read.'
            : `${isToday ? 'No register has been taken yet today.' : `No register was taken ${day}.`} Nothing here is a count of absences.`}
        </p>
      ) : (
        <>
          <DonutChart
            className="my-auto pt-3"
            layout="horizontal"
            slices={slices}
            total={counted}
            caption="Present"
            centerValue={percent === null ? '—' : `${percent}%`}
          />

          <div className="mt-4 flex items-center justify-between border-t border-line pt-3">
            <span className="text-sm font-bold text-ink-2">Total Students</span>
            <span className="text-lg font-black tabular-nums text-ink">{totals.students}</span>
          </div>

          {/* Said plainly whenever it is not the whole day: a rate over one
              register of four is a different claim from a rate over all four. */}
          {totals.markedSections < totals.totalSections && (
            <p className="mt-1.5 text-2xs font-semibold text-ink-3">
              From {totals.markedSections} of {totals.totalSections} registers — the rest are not
              taken yet.
            </p>
          )}
        </>
      )}
    </section>
  );
}
